import React, { Fragment, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import ResourceTable from "../../components/ResourceTable.jsx";
import _ from "underscore";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useQuery } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import ErrorView from "../error/errorView.jsx";
import { ActiveConstraintsBar } from "../../components/ui-components/ActiveConstraintsBar.jsx";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";

const API_URL = process.env.REACT_APP_API_URL;

const ResourceList = () => {
	// Initialize state with sessionStorage for persistence
	const [openRows] = useState(() => {
		const saved = sessionStorage.getItem("openResourceTypes");
		return saved ? saved.split(",") : [];
	});
	
	const [filters, setFilters] = useState(() => {
		const saved = sessionStorage.getItem("resourceFilters");
		if (saved) {
			try {
				return JSON.parse(saved);
			} catch (e) {
				console.warn("Failed to parse saved filters:", e);
			}
		}
		return [{
			field: "resource status",
			categoryDisplay: "Resource status",
			category: "Resource status",
			selectedValues: ["Active"],
			hideRemoveFilter: true,
			values: [],
		}];
	});
	
	const [resourceList, setResourceList] = useState(() => {
		const saved = sessionStorage.getItem("resourceList");
		if (saved) {
			try {
				return JSON.parse(saved);
			} catch (e) {
				console.warn("Failed to parse saved resourceList:", e);
			}
		}
		return [];
	});
	
	const [totalResources, setTotalResources] = useState(() => {
		const saved = sessionStorage.getItem("totalResources");
		return saved ? parseInt(saved) : 0;
	});
	const [, setOrganisations] = useState([]);
	const [selectedTagKey, ] = useState({label: "All tags", value: "All tags"});
	const [resourceKPIs, setResourceKPIs] = useState(() => {
		const saved = sessionStorage.getItem("resourceKPIs");
		if (saved) {
			try {
				return JSON.parse(saved);
			} catch (e) {
				console.warn("Failed to parse saved resourceKPIs:", e);
			}
		}
		return {
			Compute: 0,
			Storage: 0,
			Database: 0,
			Networking: 0,
		};
	});
	const { keycloak } = useKeycloak();

	// Use Zustand store for date state with moment dates
	const { startDate, endDate } = useFilterStoreWithMomentDates();
	const { selectedOrganisation, setSelectedOrganisation } = useOutletContext();
	const filtersRef = useRef();
	filtersRef.current = filters;

	// Persist filters to sessionStorage when they change
	useEffect(() => {
		sessionStorage.setItem("resourceFilters", JSON.stringify(filters));
	}, [filters]);

	// Persist openRows to sessionStorage when they change
	useEffect(() => {
		sessionStorage.setItem("openResourceTypes", openRows.join(","));
	}, [openRows]);

	// Persist resourceList to sessionStorage when it changes
	useEffect(() => {
		sessionStorage.setItem("resourceList", JSON.stringify(resourceList));
	}, [resourceList]);

	// Persist totalResources to sessionStorage when it changes
	useEffect(() => {
		sessionStorage.setItem("totalResources", totalResources.toString());
	}, [totalResources]);

	// Persist resourceKPIs to sessionStorage when it changes
	useEffect(() => {
		sessionStorage.setItem("resourceKPIs", JSON.stringify(resourceKPIs));
	}, [resourceKPIs]);

	function toggleRow(resourceType) {
		const openResourceTypes = sessionStorage.getItem("openResourceTypes");

		let openRows = openResourceTypes ? openResourceTypes.split(",") : [];

		const index = openRows.indexOf(String(resourceType));

		if (index > -1) {
			openRows = openRows.filter((row) => row !== String(resourceType));
		} else {
			openRows = [...openRows, String(resourceType)];
		}

		sessionStorage.setItem("openResourceTypes", openRows.join(","));
	}

	const buildRequest = useCallback(() => {
		let reqFilters = [];

		if (filtersRef === undefined || filtersRef.length === 0) {
			return {
				filters: [],
				start_date: startDate.format("YYYY-MM-DD"),
				end_date: endDate.format("YYYY-MM-DD"),
			};
		} else {
			filtersRef.current.forEach((filter) => {
				reqFilters.push({
					field: filter.category,
					operator: "=",
					values: filter.selectedValues,
				});
			});

			return {
				filters: reqFilters,
				start_date: startDate.format("YYYY-MM-DD"),
				end_date: endDate.format("YYYY-MM-DD"),
			};
		}
	}, [startDate, endDate]);

	const getRequestOptions = useCallback((method = "POST", filters = []) => {
		if (keycloak.token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		const requestBody = buildRequest();

		if (method === "GET") {
			return {
				method: method,
				headers: headers,
			};
		}

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		return requestOptions;
	}, [keycloak.token, buildRequest]);

	const retrieveOrganisations = useCallback(async(token) => {
		if (token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)
		const response = await fetch(API_URL + "/get-organisations", getRequestOptions("GET"));

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}, [getRequestOptions]);

	const statusOrganisations = useQuery(
		["organisations"],
		() => retrieveOrganisations(keycloak.token),
		{
			onSuccess: (data) => {
                const organisations = prepareOrganisations(data.Organisations);
                setOrganisations(organisations);
                if (selectedOrganisation === '') setSelectedOrganisation(organisations[0]);
			},
			enabled: keycloak.token !== "",
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
		}
	);	

	    /**
     * Transforms organization data into a format suitable for dropdown/select components
     * 
     * This function maps each organization element to an object with standardized fields:
     * - category: The organization level description
     * - category_description: The organization level description (duplicate for consistency)
     * - label: The organization name to display in UI
     * - value: The organization name to use as the select value
     * 
     * @param {Array} data - Array of organization objects containing level_description and name
     * @returns {Array} Array of transformed organization objects with standardized fields
     */
		const prepareOrganisations = useCallback((data) => {
			let tmpData = data
			.filter(element => element.level > 0)
			.sort((a, b) => {
				if (a.level !== b.level) {
					return a.level - b.level;
				}
				return a.name.localeCompare(b.name);
			})
			.map((element) => {
				return {
					category: element.level_description,
					category_description: element.level_description,
					label: element.name,
					value: element.id
				}
			});

			tmpData.unshift({
				category: "",
				category_description: "",
				label: "All organisations",
				value: -1
			});

			return tmpData;
		}, []);

	const retrieveResourceList = useCallback(async(token, selectedOrganisation, filtersRef) => {
		if (token === "" || selectedOrganisation.value === undefined || startDate === null || endDate === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)
		const response = await fetch(API_URL + "/get-resource-list?organisationID=" + selectedOrganisation.value, getRequestOptions("POST", filtersRef));

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}, [startDate, endDate, getRequestOptions]);

	// Create a stable key for the query that doesn't change unnecessarily
	const resourceListQueryKey = useMemo(() => [
		"resourceList", 
		selectedOrganisation?.value, 
		startDate?.format("YYYY-MM-DD"), 
		endDate?.format("YYYY-MM-DD"), 
		JSON.stringify(filters)
	], [selectedOrganisation?.value, startDate, endDate, filters]);

	const statusResourceList = useQuery(
		resourceListQueryKey,
		() => retrieveResourceList(keycloak.token, selectedOrganisation, filtersRef),
		{
			onSuccess: (data) => {
				setResourceList(prepareData(data.ResourceList));
			},
			enabled:
				keycloak.token !== "" &&
				selectedOrganisation?.value !== undefined,
			staleTime: 0, // Always consider data stale to ensure fresh data on period change
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 5 * 60 * 1000, // Keep data in cache for 5 minutes
		}
	);	

	/**
	 * Prepares resource data by grouping it hierarchically
	 * 
	 * @param {Array} data - Raw resource data array
	 * @returns {Array} Hierarchically grouped resource list with:
	 *   - Resources grouped first by resource_group
	 *   - "Other" resources grouped separately by resource_type
	 *   - "Other" group appended at the end
	 */
	const prepareData = useCallback((data) => {
		if (data == null || data.length === 0) {
			setTotalResources(0);
			return [];
		}

		const resourceList = groupResourceList(data, "resource_cluster");

		setTotalResources(resourceList.reduce((acc, resource) => acc + resource.units, 0));
		
		const otherList = resourceList.filter(resource => resource.resource_cluster === "Other");

		let groupedList;
		if (otherList.length > 0) {	
			const otherResourceList = groupResourceList(otherList[0].subRows, "resource_type");
			groupedList = resourceList.filter(resource => resource.resource_cluster !== "Other");
			
			groupedList.push({
				resource_cluster: "Other",
				units: otherList[0].units,
				subRows: otherResourceList
			});
		} else {
			groupedList = resourceList;
		}

		return groupedList;
	// groupResourceList is a useCallback declared below this hook, so naming it here would
	// evaluate it in its TDZ.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const retrieveResourceKPIs = useCallback(async(token, selectedTagKey) => {
		if (token === "" || selectedTagKey === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)

		let method = "POST";

		const requestBody = buildRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-resource-kpis", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}, [buildRequest]);

	// Create a stable key for the resource KPIs query that includes date dependencies
	const resourceKPIsQueryKey = useMemo(() => [
		"resourceKPIs", 
		selectedTagKey,
		startDate?.format("YYYY-MM-DD"), 
		endDate?.format("YYYY-MM-DD"),
		JSON.stringify(filters)
	], [selectedTagKey, startDate, endDate, filters]);

	const statusResourceKPIs = useQuery(
		resourceKPIsQueryKey,
		() => retrieveResourceKPIs(keycloak.token, selectedTagKey),
		{
			onSuccess: (data) => {
				const kpis = {};
				let total = 0;
				let totalUntagged = 0;

				data.ResourceKPIs.forEach((kpi) => {
					kpis["PercentageTagged" + kpi.resource_cluster] = ((kpi.total_resources - kpi.untagged_resources) / kpi.total_resources) * 100;
					total += kpi.total_resources;
					totalUntagged += kpi.untagged_resources;
				});

				kpis.TotalResources = total;
				kpis.TotalUntaggedResources = totalUntagged;
				kpis.PercentageTaggedTotal = ((total - totalUntagged) / total) * 100;

				setResourceKPIs(kpis);
			},
			enabled: keycloak.token !== "",
			staleTime: 0, // Always consider data stale to ensure fresh data on period change
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 5 * 60 * 1000, // Keep data in cache for 5 minutes
		}
	);

	const groupResourceList = useCallback((data, groupBy) => {
        // make sure the data is sorted and grouped by service_type
        const groupedAssets = [];
        let lastGroupBy = "";
        let lastGroup = {};

        let pdata = _.clone(data);

        pdata.sort((a, b) => {
            // Special case: if either is "Other", handle differently
            if (a[groupBy] === "Other") return 1;  // Other goes last
            if (b[groupBy] === "Other") return -1; // Other goes last
            
            // Normal alphabetical comparison for non-Other groups
            if (a[groupBy] < b[groupBy]) {
                return -1;
            }
            if (a[groupBy] > b[groupBy]) {
                return 1;
            }
            return 0;
        });


        pdata.forEach((asset) => {
            if (asset[groupBy] !== lastGroupBy) {
                lastGroupBy = asset[groupBy];
                lastGroup = {
                    [groupBy]: asset[groupBy],
                    units: 0,
					cost: 0,
					insights: 0,
                    subRows: []
                };
                groupedAssets.push(lastGroup);
            }

            lastGroup.units += asset.units;

			if (asset.cost > -1) {
				lastGroup.cost += asset.cost;
			}

			if (asset.insights > -1) {
				lastGroup.insights += asset.insights;
			}

            lastGroup.subRows.push(asset);
        });

        return groupedAssets;
    }, []);

    // Error handling for queries
    const error = statusOrganisations.error || statusResourceList.error || statusResourceKPIs.error;

    if (error) {
        return <ErrorView error={error} />;
    }

	return (
		<Fragment>
			<AdministrationHeader
				title="Resources"
				subTitle="Define and manage the tags that are used in the different cloud environments"
				className="text-text-3"
				icon={icon({name: "layer-group", style: "light"})}
				filters={filters}
				setFilters={setFilters}
				excludeCategories={["Cost component", "Cost component group", "Project"]}
			/>
			<ActiveConstraintsBar token={keycloak.token} filters={filters} setFilters={setFilters} />

			<div className="text-left flex flex-auto flex-col ml-10 mt-2 border-solid border-border">
				<div className="w-[200px] max-w-[200px] min-w-[200px] box-border flex-shrink-0">
					<KPI
						title="Total Resources"
						currentValue={totalResources}
						format="decimal"
						className="w-full max-w-full"
					/>
				</div>

				<div className="pt-2 pb-2 my-2 rounded-lg h-screen w-screen flex flex-auto flex-col">
					<ResourceTable
						data={resourceList}
						toggleRow={toggleRow}
						openRows={openRows}
					/>
				</div>
			</div>
		</Fragment>
	);
};

export default ResourceList;
